// errors.ts — jeden typ błędu dla całej warstwy sieciowej.
//
// UI nie może rozróżniać „nie ma danych” od „nie mam uprawnień” od „backend padł”
// po treści komunikatu. Rozróżnia po `kind`.

export type ApiErrorKind =
  | 'network' // brak połączenia / CORS / timeout
  | 'unauthorized' // 401 — brak lub wygasła sesja
  | 'forbidden' // 403 — sesja jest, uprawnień nie ma
  | 'not-found' // 404
  | 'rate-limited' // 429
  | 'server' // 5xx
  | 'client' // pozostałe 4xx
  | 'malformed'; // odpowiedź nie jest oczekiwanym JSON-em

export class ApiError extends Error {
  readonly kind: ApiErrorKind;
  readonly status: number | null;
  readonly url: string;
  readonly body: unknown;

  constructor(params: { kind: ApiErrorKind; message: string; status?: number | null; url: string; body?: unknown }) {
    super(params.message);
    this.name = 'ApiError';
    this.kind = params.kind;
    this.status = params.status ?? null;
    this.url = params.url;
    this.body = params.body ?? null;
  }
}

export function kindFromStatus(status: number): ApiErrorKind {
  if (status === 401) return 'unauthorized';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'not-found';
  if (status === 429) return 'rate-limited';
  if (status >= 500) return 'server';
  return 'client';
}

/** Komunikat dla operatora — po polsku, bez technicznego szumu, bez zmyślania przyczyny. */
export function describeApiError(error: unknown): string {
  if (!(error instanceof ApiError)) {
    return 'Nieoczekiwany błąd aplikacji.';
  }
  switch (error.kind) {
    case 'network':
      return 'Brak połączenia z API. Sprawdź, czy backend jest uruchomiony i czy adres środowiska jest poprawny.';
    case 'unauthorized':
      return 'Sesja wygasła albo nie jesteś zalogowany.';
    case 'forbidden':
      return 'Twoje konto nie ma uprawnień do tych danych.';
    case 'not-found':
      return 'Endpoint nie istnieje w tej wersji backendu.';
    case 'rate-limited':
      return 'Zbyt wiele zapytań — backend włączył limit. Spróbuj ponownie za chwilę.';
    case 'server':
      return `Backend zwrócił błąd (${error.status ?? '5xx'}).`;
    case 'malformed':
      return 'Backend zwrócił odpowiedź w nieoczekiwanym formacie.';
    case 'client':
      return `Zapytanie odrzucone przez backend (${error.status ?? '4xx'}).`;
  }
}
